import { IContentBlock } from "../types/iblock";
import bodyStylesManager from "../stores/body-styles-manager";
import { IHeroSlide } from "../types/content-block-data";
import { IDimensionField, IFourSideField, ITypographyRole, ITypoStyles } from "../stores/state-interfaces";
import { generateUniqueKey } from "../lib/page-builder-lib";
import pageBuilderStore from "../stores/pageBuilderStore";

type HeadingLevel = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

/**
 * `createBlockContent` – Generates a standardized `IContentBlock` object
 * based on the provided block type.
 *
 * @function
 * @description
 * This factory is used to instantiate new content blocks when a user drags an element
 * from the SidebarPanel into the DesignCanvas or a ColumnCell. It returns a fully structured
 * block with default values, wrapped in the shared interface format required by the system.
 *
 * This function ensures consistent initialization across all drop targets (root or nested),
 * and decouples block-creation logic from the canvas or UI components.
 *
 * @param {string} type – The string identifier of the block (e.g., 'text', 'heading', 'image', etc.)
 * @param {string} designBlockId – A globally unique ID that links the block to its wrapper shell
 *
 * @returns {IContentBlock} A new content block object, pre-filled with starter content
 *
 * @usedBy
 * - DesignCanvas
 * - ColumnsBlock
 *
 * @since 2025-06-15
 * @lastUpdated 2025-06-21
 */
export function createBlockContent(type: string): IContentBlock {
  const contentBlockId = crypto.randomUUID();
  const upperType = type.toUpperCase();
  const lowerType = type.toLowerCase();
	const orgInitials = pageBuilderStore.getOrgInitials();
	const uniqueKey = generateUniqueKey();	// We use these for our selector initialization.

	console.log("upperType:", upperType);

  switch (upperType) {
		case "HEADING":
			const headingDefaults = bodyStylesManager.getBodyStylesState().typography!["h1" as keyof ITypographyRole]; // or ["h2"], etc.
			
			return {
				contentBlockId,
					contentBlockRef: null,
					blockType: lowerType,
					// During initialization, we don't have a specific headingLevel yet. So we can just set it as
					// d2d-heading-[uniqueKey].
					selector: `${orgInitials}-heading-${uniqueKey}`,
					blockData: {
						html: "",
						headingLevel: 1,
						color: headingDefaults?.color || "#242424",
						fontFamily: headingDefaults?.fontFamily || "Inter, sans-serif",
						fontSize: headingDefaults?.fontSize || { value: "2", unit: "rem" },
						fontWeight: headingDefaults?.fontWeight || "700",
						lineHeight: headingDefaults?.lineHeight || { value: "1.2", unit: "rem" },
						letterSpacing: headingDefaults?.letterSpacing || { value: "0", unit: "px" },
						textDecoration: headingDefaults?.textDecoration || "none",
						textTransform: headingDefaults?.textTransform || "none",
						alignment: "left",
					}
			};
		case "TEXT":
			const bodyTextDefaults = bodyStylesManager.getBodyStylesState().typography!["p" as keyof ITypographyRole];
			
			return {
				contentBlockId: contentBlockId,
				contentBlockRef: null,
				blockType: lowerType,
				selector: `${orgInitials}-text-${uniqueKey}`,
				blockData: {
					html: "<p>&nbsp;</p>",
					color: bodyTextDefaults?.color || "#242424",
					fontFamily: bodyTextDefaults?.fontFamily || "Inter, sans-serif",
					fontSize: bodyTextDefaults?.fontSize || { value: "1.125", unit: "rem" },
					fontWeight: bodyTextDefaults?.fontWeight || "400",
					lineHeight: bodyTextDefaults?.lineHeight || { value: "1.4", unit: "rem" },
					letterSpacing: bodyTextDefaults?.letterSpacing || { value: "0", unit: "px" },
					textDecoration: bodyTextDefaults?.textDecoration || "none",
					textTransform: bodyTextDefaults?.textTransform || "none",
					alignment: "left",
				},
			};

		case "DIVIDER":
			return {
				contentBlockId: contentBlockId,
				contentBlockRef: null,
				blockType: lowerType,
				selector: `${orgInitials}-divider-${uniqueKey}`,
				blockData: {
					width: { value: "100", unit: "%" },
					height: { value: "2", unit: "px" },
					color: "#cccccc",
					padding: {
						top: "0",
						right: "0",
						bottom: "0",
						left: "0",
						unit: "px",
						linked: true,
					},
					margins: {
						top: "4",
						right: "0",
						bottom: "4",
						left: "0",
						unit: "px",
						linked: false,
					},
				},
			};

		case "BUTTON":
			return {
				contentBlockId: contentBlockId,
				contentBlockRef: null,
				blockType: lowerType,
				selector: `${orgInitials}-button-${uniqueKey}`,
				blockData: {
					label: "Button Text",                // No default text, let user type it in
					actionType: "url",    // Default to website link (or "none" if you prefer)
					url: "#",
					target: false,            // Open in same tab by default
					// Padding: 12px top/bottom, 24px left/right, linked by default
					padding: {
						top: "12",
						right: "24",
						bottom: "12",
						left: "24",
						unit: "px",
						linked: false,
					},
					// Margin: no margin by default
					margin: {
						top: "0",
						right: "0",
						bottom: "0",
						left: "0",
						unit: "px",
						linked: true,
					},
					// Border Radius: soft rounded
					borderRadius: {
						topLeft: "8",
						topRight: "8",
						bottomRight: "8",
						bottomLeft: "8",
						unit: "px",
						linked: true,
					},
					// Color palette: white, blue or subtle off-white
					backgroundColor: "#ffffff",      // or "#f6fafe" for subtle off-white, or "#2563eb" for blue
					color: "#2563eb",         // blue-600 (Tailwind default), or "#111" for dark text
					hoverBackgroundColor: "#2563eb",
					hoverColor: "#ffffff",
					borderColor: "#2563eb",       // match text color for default outline
					borderWidth: {value: "1", unit: "px"}, // ,             // 1px border
					borderStyle: "solid",
					// Typography
					fontSize: {value: "1.125", unit: "rem"},               // px
					fontWeight: "600",            // semi-bold
					letterSpacing: {value: "0", unit: "px"},           // normal
					lineHeight: {value: "1.5", unit: "rem"},            // good default for buttons
					fontFamily: "Inter, sans-serif",        // fallback to app's default
					width: { value: "150", unit: "px" },
					alignment: "center"
				}
			};

		case "COLUMNS":
			return {
				contentBlockId: contentBlockId,
				contentBlockRef: null,
				blockType: lowerType,
				selector: `${orgInitials}-columns-${uniqueKey}`,
				blockData: {
					children: [[], []],
					columnWidths: [0.5, 0.5],
					// NEW: Padding defaults (good UX for most layouts)
					cellPadding: {
						top: "8",
						right: "8",
						bottom: "8",
						left: "8",
						unit: "px",
						linked: true,
					},
					// NEW: Margin defaults (optional, but nice to be explicit)
					cellMargin: {
						top: "0",
						right: "0",
						bottom: "0",
						left: "0",
						unit: "px",
						linked: true,
					},
					cellRadius: {
						topLeft: "8",
						topRight: "8",
						bottomRight: "8",
						bottomLeft: "8",
						unit: "px",
						linked: true,
					},
					// NEW: Gap defaults (optional)
					columnGap: {
						value: "16",
						unit: "px",
					},
					rowGap: {
						value: "16",
						unit: "px",
					},
					// You can add more fields as needed, like borderRadius, background, etc.
				},
			};

		case "IMAGE":
			return {
				contentBlockId: contentBlockId,
				contentBlockRef: null,
				blockType: lowerType,
				selector: `${orgInitials}-image-${uniqueKey}`,
				blockData: {
					src: "https://placehold.co/600x400",
					alt: "",
					caption: "",
					description: "",
					margin: {
						top: "0",
						right: "0",
						bottom: "0",
						left: "0",
						unit: "px",
						linked: true,
					},
					borderRadius: {
						topLeft: "0",
						topRight: "0",
						bottomRight: "0",
						bottomLeft: "0",
						unit: "px",
						linked: true,
					},
					width: { value: "100", unit: "%" },    // Default full width
					height: { value: "", unit: "px" },     // Empty = auto
					alignment: "center",
					imageFit: "cover",
					imagePosition: "center"
				},
			};

		case "HERO_SLIDER":
			const px = (value: number): IDimensionField => ({ value: value.toString(), unit: "px" });
			const rem = (value: number): IDimensionField => ({ value: value.toString(), unit: "rem" });
			const percent = (value: number): IDimensionField => ({ value: value.toString(), unit: "%" });
			// During block initialization, we will generate two default slides, so we will generate the necessary 
			// default unique keys here.
			const slide01Key = generateUniqueKey();
			const slide02Key = generateUniqueKey();
			
			/**
			 * Creates a four-sided dimension object (for margin/padding) with explicit unit.
			 */
			const fourSidedDimen = (top: string, right: string, bottom: string, left: string, unit: string): IFourSideField => ({
				top, right, bottom, left, unit, linked: true,
			});

			return {
				contentBlockId,
				contentBlockRef: null,
				blockType: lowerType,
				selector: `${orgInitials}-hero-slider-${uniqueKey}`,
				blockData: {
					slides: [
						{
							slideImage: {
								src: "img/vecteezy-protein-shake-on-a-bench-in-a-modern-gym-environment.jpg",
								alt: "Protein shake on a bench in a modern gym environment",
								// Optionally add caption, url, openInNewTab if needed
								actionType: "url",
								url: "#",
								openInNewTab: false,
								selector: `${orgInitials}-slide-img-${slide01Key}`,
								imageFit: "cover",
								imagePosition: "center",
							},
							overlayColor: "rgba(0,0,0,0.5)",
							isHeroMode: true,
							heroHeader: {
								selector: `${orgInitials}-hero-header-${slide01Key}`,
								text: "Transform Your Fitness Journey",
								fontFamily: "Montserrat, sans-serif",
								color: "#ffffff",
								fontSize: rem(2),                // 2rem
								lineHeight: rem(1.2),            // 1.2rem (or use a multiplier as per your style)
								letterSpacing: px(0),
								margin: {
									top: "0",
									right: "0",
									bottom: "0",
									left: "0",
									unit: "px",
									linked: true,
								},
								padding: fourSidedDimen("0", "0", "0", "0", "px"),
								fontWeight: "700",
								textDecoration: "none",
								textTransform: "none",
								alignment: "center",
							},
							heroText: {
								selector: `${orgInitials}-hero-text-${slide01Key}`,
								text: "Join us for personalized coaching, state-of-the-art equipment, and a vibrant community. Your next level starts here.",
								fontFamily: "Inter, sans-serif",
								color: "#ffffff",
								fontSize: rem(1),
								lineHeight: rem(1.5),
								letterSpacing: px(0),
								margin: fourSidedDimen("0", "0", "1.5", "0", "rem"),
								padding: fourSidedDimen("0", "0", "0", "0", "px"),
								fontWeight: "400",
								textDecoration: "none",
								textTransform: "none",
								alignment: "center",
							},
							cta: {
								selector: `${orgInitials}-hero-cta-${slide01Key}`,
								text: "Book a Free Trial",
								url: "#",
								alignment: "center",
								openInNewTab: false,
							},
						},
						{
							slideImage: {
								src: "img/vecteezy_neatly-arranged-sports-gear-in-an-open-gym-locker.jpg",
								alt: "Neatly arranged sports gear in a gym locker",
								selector: `${orgInitials}-slide-img-${slide02Key}`,
								imageFit: "cover",
								imagePosition: "center",
							},
							overlayColor: "rgba(22,41,74,0.55)",
							isHeroMode: true,
							heroHeader: {
								selector: `${orgInitials}-hero-header-${slide02Key}`,
								text: "All You Need. In One Place.",
								fontFamily: "Montserrat, sans-serif",
								color: "#fff",
								fontSize: rem(2),
								lineHeight: rem(1.2),
								letterSpacing: px(0),
								margin: fourSidedDimen("0", "0", "1", "0", "rem"),
								padding: fourSidedDimen("0", "0", "0", "0", "px"),
								fontWeight: "700",
								textDecoration: "none",
								textTransform: "none",
								alignment: "center",
							},
							heroText: {
								selector: `${orgInitials}-hero-text-${slide02Key}`,
								text: "Access premium facilities and all the essentials you need. We keep your focus on progress—not logistics.",
								fontFamily: "Inter, sans-serif",
								color: "#fff",
								fontSize: rem(1),
								lineHeight: rem(1.5),
								letterSpacing: px(0),
								margin: fourSidedDimen("0", "0", "1.5", "0", "rem"),
								padding: fourSidedDimen("0", "0", "0", "0", "px"),
								fontWeight: "400",
								textDecoration: "none",
								textTransform: "none",
								alignment: "center",
							},
							cta: {
								selector: `${orgInitials}-hero-cta-${slide02Key}`,
								text: "See Our Facilities",
								url: "#",
								alignment: "center",
								openInNewTab: false,
							},
						},
					],
					showArrows: true,
					showIndicators: true,
					autoplay: false,
					autoplayInterval: 3000,
					loop: true,
					width: percent(100),
					height: px(400),
					borderRadius: px(12),
					backgroundColor: "#f8fafc",
				},
			};

		case "VIDEO":
			return {
				contentBlockId: contentBlockId,
				contentBlockRef: null,
				blockType: lowerType,
				selector: `${orgInitials}-video-${uniqueKey}`,
				blockData: {
					url: "https://www.youtube.com/embed/dQw4w9WgXcQ",
					padding: {
						value: "0",
						unit: "px"
					}
				},
			};

		/**
		 * Generates the default data structure for a new Socials/Sharing Content Block,
		 * including a unique selector for CSS targeting.
		 *
		 * All fields are explicitly initialized to prevent undefined/null issues,
		 * following PageBuilder type-safety conventions.
		 *
		 * @param contentBlockId - Unique identifier for the block instance.
		 * @returns {IContentBlock} Initialized Socials block.
		 */
		case "SOCIALS":
			const socialsBlockKey = "-" + generateUniqueKey();
			return {
				contentBlockId,
				contentBlockRef: null,
				blockType: lowerType,
				selector: `${orgInitials}-socials-${uniqueKey}`,
				blockData: {
					socials: [
						{ type: "facebook", url: "https://facebook.com" },
						{ type: "twitter", url: "https://twitter.com" },
						{ type: "instagram", url: "https://instagram.com" },
					],
					iconShape: "circle",
					iconSize: 32,
					alignment: "left",
					spacing: 8,
				},
			};

		case "HTML":
			return {
				contentBlockId: contentBlockId,
				contentBlockRef: null,
				blockType: lowerType,
				selector: `${orgInitials}-html-${uniqueKey}`,
				blockData: {
					html: "<div>Custom HTML Content</div>",
					padding: {
						value: "0",
						unit: "px"
					}
				},
			};

		default:
			return {
				contentBlockId: contentBlockId,
				contentBlockRef: null,
				blockType: "text",
				blockData: {
					html: "<p>Unknown content type dropped.</p>",
				},
			};
	}
}

/**
 * Generates and returns a default Hero Slider slide object for the specified index.
 * Used to initialize or append new slides in the HeroSlider block with sensible defaults.
 *
 * @param {number} [index=1] - The index to use for the slide (e.g., for display or naming purposes).
 * @returns {IHeroSlide} The default-initialized Hero Slide object.
 *
 * @example
 * const firstSlide = getDefaultHeroSlide();    // Defaults to index 1
 * const thirdSlide = getDefaultHeroSlide(3);   // Creates a slide for index 3
 */
export function getDefaultHeroSlide(index = 1): IHeroSlide {
	const orgInitials = pageBuilderStore.getOrgInitials();
	const uniqueKey	= generateUniqueKey();

  return {
    isHeroMode: false,
    slideImage: {
      src: "https://placehold.co/600x400?text=NEW",
      alt: `Placeholder image ${index}`,
      caption: "",
      description: "",
      url: "",
      openInNewTab: false,
    },
    overlayColor: "rgba(0,0,0,0.5)",
    heroHeader: {
			selector: `${orgInitials}-hero-header-${uniqueKey}`,
      text: "",
      fontFamily: "Montserrat, sans-serif",
      color: "#fff",
      fontSize: { value: "2", unit: "rem" },
      lineHeight: { value: "1.2", unit: "rem" },
      letterSpacing: { value: "0", unit: "px" },
      margin: { top: "0", right: "0", bottom: "1", left: "0", unit: "rem", linked: true },
      padding: { top: "0", right: "0", bottom: "0", left: "0", unit: "rem", linked: true },
      fontWeight: "700",
      textDecoration: "none",
      textTransform: "none",
      alignment: "center",
    },
    heroText: {
			selector: `${orgInitials}-hero-text-${uniqueKey}`,
      text: "",
      fontFamily: "Inter, sans-serif",
      color: "#fff",
      fontSize: { value: "1", unit: "rem" },
      lineHeight: { value: "1.5", unit: "rem" },
      letterSpacing: { value: "0", unit: "px" },
      margin: { top: "0", right: "0", bottom: "1.5", left: "0", unit: "rem", linked: true },
      padding: { top: "0", right: "0", bottom: "0", left: "0", unit: "rem", linked: true },
      fontWeight: "400",
      textDecoration: "none",
      textTransform: "none",
      alignment: "center",
    },
    cta: {
			selector: `${orgInitials}-hero-cta-${uniqueKey}`,
      text: "",
      url: "",
      openInNewTab: false,
      alignment: "center"
    }
  };
}
